import { isNumber } from "lodash-es";
import { useEffect, useState } from "react";

import { useApi } from "@hooks/index";
import { Api } from "@Shared/schemas";

import { type NextChallengeData, type SessionNavigationData } from "./use-session-results.types";

interface UseNextChallengeProps {
  sessionData: SessionNavigationData | null;
  challengeId: string | undefined;
  userId: string | undefined;
  userRating: number | undefined;
}

export const useNextChallenge = ({ sessionData, challengeId, userId, userRating }: UseNextChallengeProps) => {
  const [nextChallenge, setNextChallenge] = useState<NextChallengeData | null>(null);
  const [showNextChallengeCta, setShowNextChallengeCta] = useState(false);

  const { call: getNextChallenge } = useApi<typeof Api.Challenges.GetNextChallenge>({
    api: Api.Challenges.GetNextChallenge,
    disableLoader: true,
    request: {
      user_id: userId!,
      user_rating: userRating!,
    },
  });

  useEffect(() => {
    const tags = sessionData?.tags;
    const challengeIdNumber = Number(challengeId);
    if (!Array.isArray(tags) || tags.length === 0 || !Number.isFinite(challengeIdNumber) || !userId || !isNumber(userRating)) {
      setNextChallenge(null);
      setShowNextChallengeCta(false);
      return;
    }

    let isCancelled = false;

    getNextChallenge({
      user_id: userId,
      ids: [challengeIdNumber],
      include_tags: tags.map(tagObj => tagObj.tag),
      user_rating: userRating,
    })
      .then(response => {
        if (isCancelled || !response?.data) {
          return;
        }

        setNextChallenge(response.data as NextChallengeData);
        setShowNextChallengeCta(true);
      })
      .catch(error => {
        if (isCancelled) {
          return;
        }

        console.warn("Warning: Unable to get next challenge", error);
      });

    return () => {
      isCancelled = true;
    };
    // `getNextChallenge` can be recreated by useApi internals; keep deps on stable inputs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeId, sessionData?.tags, userId, userRating]);

  return { nextChallenge, showNextChallengeCta };
};
